/* eslint-disable no-console */
import types from '../mutation-types';
import PipelineAPI from '../../api/pipelines';
import { handlePipelineError } from '../../routes/dashboard/pipeline/helpers/pipelineErrorHandler';
import { createAccountRequestGuard } from './utils/accountRequestGuard';
import { createOperationRequestTracker } from './utils/operationRequestTracker';

const fetchRequestGuard = createAccountRequestGuard();
const mutationRequestTracker = createOperationRequestTracker();
const inFlightFetches = new WeakMap();
const normalizeAccountId = accountId => {
  if (accountId === undefined || accountId === null) return null;
  const normalized = String(accountId).trim();
  return normalized || null;
};
const resolveAccountId = (rootGetters, accountId) =>
  normalizeAccountId(
    accountId === undefined ? rootGetters.getCurrentAccountId : accountId
  );
const requireAccountId = (rootGetters, accountId) => {
  const resolvedAccountId = resolveAccountId(rootGetters, accountId);
  if (resolvedAccountId === null) {
    throw new TypeError('accountId is required');
  }
  return resolvedAccountId;
};
const accountIsCurrent = (rootGetters, accountId) => {
  const activeAccountId = normalizeAccountId(rootGetters.getCurrentAccountId);
  return (
    accountId !== null &&
    activeAccountId !== null &&
    activeAccountId === accountId
  );
};

const state = {
  requestEpoch: 0,
  loadedAccountId: null,
  fetchingAccountId: null,
  records: [],
  selectedPipeline: null,
  isPipelineEnabled: false,
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
  },
};

const getters = {
  getPipelines: $state => $state.records,
  getSelectedPipeline: $state => $state.selectedPipeline,
  getUIFlags: $state => $state.uiFlags,
  isPipelineEnabled: $state => $state.isPipelineEnabled,
  getPipelineById: $state => id => {
    return $state.records.find(pipeline => String(pipeline.id) === String(id));
  },
};

const actions = {
  async fetch(
    { commit, dispatch, rootGetters, state: $state },
    { force = false, accountId: rawAccountId, generation } = {}
  ) {
    const accountId = requireAccountId(rootGetters, rawAccountId);
    const hasUnscopedRecords =
      $state.loadedAccountId === null &&
      ($state.records.length > 0 || $state.selectedPipeline !== null);
    const hasDifferentLoadedAccount =
      $state.loadedAccountId !== null && $state.loadedAccountId !== accountId;
    const hasDifferentFetchingAccount =
      $state.fetchingAccountId !== null &&
      $state.fetchingAccountId !== accountId;

    if (
      hasUnscopedRecords ||
      hasDifferentLoadedAccount ||
      hasDifferentFetchingAccount
    ) {
      commit('RESET_ACCOUNT_STATE');
    }

    const activeFetch = inFlightFetches.get($state);
    if (!force && activeFetch?.accountId === accountId) {
      return activeFetch.promise;
    }
    if (!force && $state.loadedAccountId === accountId) {
      return $state.records;
    }

    const request = fetchRequestGuard.begin($state, {
      accountId,
      generation,
    });
    const requestEpoch = $state.requestEpoch;
    const isCurrentRequest = () =>
      requestEpoch === $state.requestEpoch &&
      fetchRequestGuard.isCurrent($state, request, rootGetters);
    commit('SET_PIPELINE_FETCHING_ACCOUNT', accountId);
    commit(types.SET_PIPELINE_UI_FLAG, { isFetching: true });
    const fetchPromise = Promise.resolve().then(async () => {
      try {
        const { data } = await PipelineAPI.get(accountId);
        if (!isCurrentRequest()) return [];
        const formattedData = data.map(pipeline => ({
          ...pipeline,
          id: String(pipeline.id),
          stages: Object.entries(pipeline.stages || {}).reduce(
            (acc, [key, stage]) => ({
              ...acc,
              [String(key)]: {
                ...stage,
                position: parseInt(stage.position, 10) || 0,
              },
            }),
            {}
          ),
        }));
        commit(types.SET_PIPELINES, {
          accountId,
          records: formattedData,
        });

        // Se não houver pipeline selecionado, seleciona o primeiro.
        if (!$state.selectedPipeline && formattedData.length > 0) {
          await dispatch('setSelectedPipeline', formattedData[0]);
        } else if ($state.selectedPipeline) {
          // Já HÁ um selecionado — re-sincroniza a partir dos registros novos.
          //
          // O board não lê `records`: ele lê `getSelectedPipeline`. Sem este
          // ramo, um `fetch` atualizava a lista e deixava o selecionado com os
          // dados antigos, então editar a cor de uma etapa em "editar funil"
          // salvava no banco e o board seguia mostrando a cor velha até um
          // reload completo da página. `handleEdit` do PipelinesManager chama
          // este fetch logo após salvar, que é exatamente o caminho do bug.
          //
          // Não recorre a `formattedData[0]` quando o selecionado sumiu da
          // lista (funil apagado por outra aba): trocar o funil aberto por
          // outro sem o usuário pedir é pior que manter o estado obsoleto.
          // REMOVE_PIPELINE_REMOTE trata a remoção.
          const refreshed = formattedData.find(
            pipeline =>
              String(pipeline.id) === String($state.selectedPipeline.id)
          );
          if (refreshed) await dispatch('setSelectedPipeline', refreshed);
        }

        return formattedData;
      } catch (error) {
        if (!isCurrentRequest()) return [];
        console.error('Error fetching pipelines:', error);
        return handlePipelineError(error);
      } finally {
        if (isCurrentRequest()) {
          commit('SET_PIPELINE_FETCHING_ACCOUNT', null);
          commit(types.SET_PIPELINE_UI_FLAG, { isFetching: false });
        }
        if (inFlightFetches.get($state)?.request === request) {
          inFlightFetches.delete($state);
        }
      }
    });
    inFlightFetches.set($state, { accountId, promise: fetchPromise, request });
    return fetchPromise;
  },

  async setSelectedPipeline({ commit }, pipeline) {
    if (!pipeline?.id) {
      console.error('Attempt to select invalid pipeline:', pipeline);
      return;
    }

    try {
      commit(types.SET_PIPELINE_UI_FLAG, { isUpdating: true });

      // Salva o ID do pipeline in localStorage
      // localStorage.setItem(STORAGE_KEY, String(pipeline.id));

      commit(types.SET_SELECTED_PIPELINE, pipeline);
    } finally {
      commit(types.SET_PIPELINE_UI_FLAG, { isUpdating: false });
    }
  },

  setPipelineEnabled({ commit }, enabled) {
    commit(types.SET_PIPELINE_ENABLED, enabled);
  },

  async createPipeline(
    { commit, rootGetters, state: $state },
    rawPayload = {}
  ) {
    const accountId = requireAccountId(rootGetters, rawPayload.accountId);
    const requestEpoch = $state.requestEpoch;
    const operation = mutationRequestTracker.begin($state, {
      group: 'isCreating',
    });
    const requestIsCurrent = () =>
      requestEpoch === $state.requestEpoch &&
      accountIsCurrent(rootGetters, accountId) &&
      mutationRequestTracker.isLatest($state, operation);
    const payload = { ...rawPayload };
    delete payload.accountId;
    commit(types.SET_PIPELINE_UI_FLAG, { isCreating: true });
    try {
      const { data } = await PipelineAPI.create(payload, accountId);
      if (!requestIsCurrent()) return undefined;
      commit('ADD_PIPELINE_REMOTE', data);
      return data;
    } finally {
      const completion = mutationRequestTracker.finish($state, operation);
      if (
        completion.isCurrentTracker &&
        completion.activeCount === 0 &&
        requestEpoch === $state.requestEpoch &&
        accountIsCurrent(rootGetters, accountId)
      ) {
        commit(types.SET_PIPELINE_UI_FLAG, { isCreating: false });
      }
    }
  },

  async updatePipeline(
    { commit, rootGetters, state: $state },
    { id, accountId: rawAccountId, ...payload }
  ) {
    const accountId = requireAccountId(rootGetters, rawAccountId);
    const requestEpoch = $state.requestEpoch;
    const operation = mutationRequestTracker.begin($state, {
      group: 'isUpdating',
      identity: id,
    });
    const requestIsCurrent = () =>
      requestEpoch === $state.requestEpoch &&
      accountIsCurrent(rootGetters, accountId) &&
      mutationRequestTracker.isLatest($state, operation);
    commit(types.SET_PIPELINE_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await PipelineAPI.update(id, payload, accountId);
      // UPDATE_PIPELINE_REMOTE already updates selectedPipeline if IDs match
      if (!requestIsCurrent()) return undefined;
      commit('UPDATE_PIPELINE_REMOTE', data);
      return data;
    } finally {
      const completion = mutationRequestTracker.finish($state, operation);
      if (
        completion.isCurrentTracker &&
        completion.activeCount === 0 &&
        requestEpoch === $state.requestEpoch &&
        accountIsCurrent(rootGetters, accountId)
      ) {
        commit(types.SET_PIPELINE_UI_FLAG, { isUpdating: false });
      }
    }
  },

  async deletePipeline({ commit, rootGetters, state: $state }, rawPayload) {
    const payload =
      rawPayload && typeof rawPayload === 'object'
        ? rawPayload
        : { id: rawPayload };
    const pipelineId = payload.id;
    const accountId = requireAccountId(rootGetters, payload.accountId);
    const requestEpoch = $state.requestEpoch;
    const operation = mutationRequestTracker.begin($state, {
      group: 'delete',
      identity: pipelineId,
    });
    try {
      await PipelineAPI.delete(pipelineId, accountId);
      if (
        requestEpoch === $state.requestEpoch &&
        accountIsCurrent(rootGetters, accountId) &&
        mutationRequestTracker.isLatest($state, operation)
      ) {
        commit('REMOVE_PIPELINE_REMOTE', pipelineId);
      }
    } finally {
      mutationRequestTracker.finish($state, operation);
    }
  },

  reset: ({ commit }) => {
    commit('RESET_ACCOUNT_STATE');
  },
};

const mutations = {
  [types.SET_PIPELINES]($state, { accountId, records }) {
    $state.records = records;
    $state.loadedAccountId = accountId;
  },
  SET_PIPELINE_FETCHING_ACCOUNT($state, accountId) {
    $state.fetchingAccountId = accountId;
  },
  [types.SET_SELECTED_PIPELINE]($state, pipeline) {
    $state.selectedPipeline = pipeline;
  },
  [types.SET_PIPELINE_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.SET_PIPELINE_ENABLED]($state, enabled) {
    $state.isPipelineEnabled = enabled;
  },
  RESET_ACCOUNT_STATE($state) {
    inFlightFetches.delete($state);
    mutationRequestTracker.reset($state);
    $state.requestEpoch = ($state.requestEpoch || 0) + 1;
    $state.loadedAccountId = null;
    $state.fetchingAccountId = null;
    $state.records = [];
    $state.selectedPipeline = null;
    $state.isPipelineEnabled = false;
    $state.uiFlags = {
      isFetching: false,
      isCreating: false,
      isUpdating: false,
    };
  },

  // === REAL-TIME MUTATIONS (from WebSocket) ===

  // Add pipeline from remote (other user created)
  ADD_PIPELINE_REMOTE($state, pipeline) {
    const exists = $state.records.find(
      p => String(p.id) === String(pipeline.id)
    );
    if (!exists && pipeline.id) {
      const formattedPipeline = {
        ...pipeline,
        id: String(pipeline.id),
        stages: Object.entries(pipeline.stages || {}).reduce(
          (acc, [key, stage]) => ({
            ...acc,
            [String(key)]: {
              ...stage,
              position: parseInt(stage.position, 10) || 0,
            },
          }),
          {}
        ),
      };
      $state.records.push(formattedPipeline);
    }
  },

  // Update pipeline from remote (other user updated)
  UPDATE_PIPELINE_REMOTE($state, pipeline) {
    const index = $state.records.findIndex(
      p => String(p.id) === String(pipeline.id)
    );
    if (index !== -1) {
      const formattedPipeline = {
        ...$state.records[index],
        ...pipeline,
        id: String(pipeline.id),
        stages: pipeline.stages
          ? Object.entries(pipeline.stages).reduce(
              (acc, [key, stage]) => ({
                ...acc,
                [String(key)]: {
                  ...stage,
                  position: parseInt(stage.position, 10) || 0,
                },
              }),
              {}
            )
          : $state.records[index].stages,
      };
      $state.records.splice(index, 1, formattedPipeline);

      // Update selectedPipeline if it's the same
      if (
        $state.selectedPipeline &&
        String($state.selectedPipeline.id) === String(pipeline.id)
      ) {
        $state.selectedPipeline = formattedPipeline;
      }
    }
  },

  // Remove pipeline from remote (other user deleted)
  REMOVE_PIPELINE_REMOTE($state, pipelineId) {
    $state.records = $state.records.filter(
      p => String(p.id) !== String(pipelineId)
    );

    // If deleted pipeline was selected, clear selection
    if (
      $state.selectedPipeline &&
      String($state.selectedPipeline.id) === String(pipelineId)
    ) {
      $state.selectedPipeline =
        $state.records.length > 0 ? $state.records[0] : null;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
